import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { ChevronDown, PartyPopper, Settings, Sparkles } from "lucide-react";
import { PAYROLL_SETUP_TABS, PAYROLL_ADVANCED_TABS } from "./payrollSetupNav";
import { useSetupProgress } from "../../Context/SetupProgressContext";
import "../../../styles/payroll-setup.css";
import "../../../styles/payroll-setup-wizard.css";

function resolveActiveSetupTab(pathname) {
  return PAYROLL_SETUP_TABS.find(
    (t) => pathname === t.to || pathname.startsWith(`${t.to}/`)
  );
}

function isComponentEditorPath(pathname) {
  if (pathname === "/salary-v2/components/new") return true;
  return /^\/salary-v2\/components\/[^/]+\/edit\/?$/.test(pathname);
}

export default function SalaryV2Home() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const { progress } = useSetupProgress() || {};
  const payrollComplete = progress?.payrollReadiness?.complete === true;
  const payrollIncomplete = progress?.payrollReadiness && !progress.payrollReadiness.complete;

  // Always keep Settings-linked pages reachable — never force-redirect to Pay Groups.
  const visibleTabs = useMemo(() => PAYROLL_SETUP_TABS, []);

  const moreActive = PAYROLL_ADVANCED_TABS.some((t) => location.pathname.startsWith(t.to));
  const onPayGroupsPage = location.pathname.startsWith("/salary-v2/pay-groups");
  const onFormulaBuilderPage = location.pathname.startsWith("/salary-v2/formula-builder");
  const onStructuresPage = location.pathname.startsWith("/salary-v2/structures");
  const inSalaryStructurePipeline = new URLSearchParams(location.search).has("pipeline");
  const hidePipelineChrome = inSalaryStructurePipeline && (onFormulaBuilderPage || onStructuresPage);
  const onExtraWorkPage = location.pathname.startsWith("/salary-v2/extra-work");
  const onOvertimeWorksPage = location.pathname.startsWith("/salary-v2/overtime");
  const onOtFormulaBuilderPage = location.pathname.startsWith("/salary-v2/overtime/formula");
  const onPolicyWorksPage = onExtraWorkPage || onOvertimeWorksPage;
  const onPayrollCalendarPage = location.pathname.startsWith("/salary-v2/payroll-calendar");
  const onComponentsHubPage = location.pathname === "/salary-v2/components";
  const activeSetupTab = resolveActiveSetupTab(location.pathname);
  const onComponentEditorPage = isComponentEditorPath(location.pathname);
  const activeMoreTab = PAYROLL_ADVANCED_TABS.find((t) => location.pathname.startsWith(t.to));

  const pageTitle = onPayrollCalendarPage
    ? "Payroll Calendar"
    : activeSetupTab
      ? (payrollComplete
        ? (activeSetupTab.shortLabel?.replace(/^\d+\.\d+\s*/, "") || activeSetupTab.label)
        : activeSetupTab.label)
      : (activeMoreTab?.label || (payrollComplete ? "Payroll Tools" : "Payroll Setup"));

  const pageSubtitle = onPayrollCalendarPage
    ? "Organisation working days, holidays, attendance, and payroll dates."
    : onPayGroupsPage
    ? "Group employees by pay cycle so each team is paid on the right schedule."
    : activeSetupTab?.id === "components"
      ? "Earnings, deductions, and reimbursements used in payroll runs."
      : activeSetupTab?.id === "structures"
        ? "Reusable salary structures assigned to employees."
        : activeSetupTab?.id === "settings"
          ? "Pay cycle, processing day, payment date, and default structure."
          : activeSetupTab?.id === "overtime"
            ? "Enable OT, hours source, OT pay calculation, eligibility, and OT payroll approval."
          : activeSetupTab?.id === "extra-work"
            ? "Sunday work, holiday work, premium multipliers, and compensatory off."
          : payrollComplete
            ? "Advanced payroll tools — switch pages anytime from the tabs above."
            : "Configure payroll settings, components, formulas, salary structures, and pay groups.";

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    function onDown(e) {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setMoreOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  // Index route only redirects — avoid flashing the full setup chrome.
  if (location.pathname === "/salary-v2" || location.pathname === "/salary-v2/") {
    return <Outlet />;
  }

  const hidePageHeader =
    onFormulaBuilderPage || onOtFormulaBuilderPage || moreActive || onComponentEditorPage || onComponentsHubPage;

  return (
    <div className={`ps-page bw-page${onPolicyWorksPage || onPayrollCalendarPage ? " ps-page--extra-work" : ""}${onComponentEditorPage ? " ps-page--component-editor" : ""}${onComponentsHubPage ? " ps-page--components-hub" : ""}`}>
      {!hidePageHeader ? (
        <header
          className="bw-page-header ps-page-header ps-page-header--compact"
          style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}
        >
          <div>
            <h1 className="bw-page-title ps-page-title">{pageTitle}</h1>
            <p className="bw-page-subtitle ps-page-subtitle">{pageSubtitle}</p>
          </div>
          {onPayrollCalendarPage ? (
            <Link
              to="/salary-v2/setup"
              className="ps-wiz-btn ps-wiz-btn--ghost ps-extra-work-settings-link"
              style={{ textDecoration: "none", marginLeft: "auto" }}
            >
              <Settings size={15} /> Payroll Settings
            </Link>
          ) : onPolicyWorksPage ? (
            <Link
              to="/settings"
              className="ps-wiz-btn ps-wiz-btn--ghost ps-extra-work-settings-link"
              style={{ textDecoration: "none", marginLeft: "auto" }}
            >
              <Settings size={15} /> All Settings
            </Link>
          ) : payrollIncomplete ? (
            <Link to="/salary-v2/setup" className="ps-wiz-btn ps-wiz-btn--primary" style={{ textDecoration: "none", marginLeft: "auto" }}>
              <Settings size={15} /> Payroll Settings
            </Link>
          ) : null}
        </header>
      ) : null}

      {payrollComplete && onPayGroupsPage ? (
        <div className="ps-congrats-banner" role="status">
          <span className="ps-congrats-icon-wrap" aria-hidden="true">
            <PartyPopper size={20} />
          </span>
          <div>
            <strong>Payroll setup complete 🎉</strong>
            <p>
              Your settings are ready. Create pay groups below so employees can be paid on the correct cycle.
            </p>
          </div>
        </div>
      ) : null}

      {payrollIncomplete && !onPayrollCalendarPage && !onComponentEditorPage && !onFormulaBuilderPage && !moreActive && !onComponentsHubPage && !hidePipelineChrome ? (
        <div className="ps-wiz-banner ps-wiz-banner--info ps-wiz-banner--compact">
          <Sparkles size={14} />
          <span>
            Payroll setup is {progress.payrollReadiness.percent}% complete —{" "}
            {progress.payrollReadiness.current_step_label || "continue configuration"}.
          </span>
          <Link to="/salary-v2/setup" style={{ marginLeft: "auto", fontWeight: 700, color: "inherit", fontSize: "0.8rem" }}>
            Open payroll settings →
          </Link>
        </div>
      ) : null}

      {!onPolicyWorksPage && !onPayrollCalendarPage && !onComponentEditorPage && !onFormulaBuilderPage && !moreActive && !onComponentsHubPage && !onStructuresPage && !hidePipelineChrome ? (
        <nav className={`ps-tabs${payrollComplete ? " ps-tabs--complete" : ""}`} aria-label="Payroll setup">
          {visibleTabs.map((t) => (
            <NavLink
              key={t.id}
              to={t.to}
              end={t.end}
              className={({ isActive }) => `ps-tab${isActive ? " active" : ""}`}
            >
              {payrollComplete ? (t.shortLabel?.replace(/^\d+\.\d+\s*/, "") || t.label) : t.label}
            </NavLink>
          ))}

          <div ref={moreRef} className={`ps-tab-more-wrap${payrollComplete ? " ps-tab-more-wrap--prominent" : ""}`}>
            <button
              type="button"
              className={`ps-tab-more${moreActive ? " active" : ""}${payrollComplete ? " ps-tab-more--prominent" : ""}`}
              aria-haspopup="true"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((o) => !o)}
            >
              More
              <ChevronDown
                size={14}
                style={{ transform: moreOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}
              />
            </button>

            {moreOpen ? (
              <div className="ps-tab-more-menu" role="menu">
                {PAYROLL_ADVANCED_TABS.map((t) => (
                  <NavLink
                    key={t.to}
                    to={t.to}
                    role="menuitem"
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) => `ps-tab-more-link${isActive ? " active" : ""}`}
                  >
                    {t.label}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>
        </nav>
      ) : null}

      <div className="ps-panel">
        <Outlet />
      </div>
    </div>
  );
}
